
import { supabase } from "@/integrations/supabase/client";
import { Profile, UserRole } from "@/types/user";

/**
 * Ensures a specific email has admin privileges
 * This will either update an existing user to be an admin or create a new admin user if needed
 */
export async function ensureAdminUser(email: string): Promise<{ success: boolean; message: string; existingEmail?: string }> {
  try {
    // First check if the user already exists (using case-insensitive comparison)
    const { data: existingUser, error: lookupError } = await supabase
      .from('profiles')
      .select('*')
      .ilike('email', email)
      .single();

    if (lookupError && lookupError.code !== 'PGRST116') {
      // Real error (not just "no rows returned")
      throw lookupError;
    }

    if (existingUser) {
      // User exists, check role
      if (existingUser.role === 'admin') {
        return { 
          success: true, 
          message: `User ${existingUser.email} is already an admin`,
          existingEmail: existingUser.email 
        };
      }

      // User exists but is not an admin
      return { 
        success: false, 
        message: `User ${existingUser.email} exists but is not an admin. Would you like to update their role?`,
        existingEmail: existingUser.email
      };
    } else {
      // User doesn't exist yet
      return { 
        success: false, 
        message: `User ${email} does not exist in the system. Please create this user with admin role.` 
      };
    }
  } catch (error: any) {
    console.error("Error ensuring admin user:", error);
    return { 
      success: false, 
      message: `Error ensuring admin privileges: ${error.message}` 
    };
  }
}

/**
 * Updates an existing user's role to admin
 */
export async function updateUserToAdmin(email: string): Promise<{ success: boolean; message: string }> {
  try {
    // First update the profile table
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ role: 'admin' as UserRole })
      .ilike('email', email);
    
    if (profileError) throw profileError;
    
    // Next, update the user metadata in auth.users
    // Get the user id from the profiles table
    const { data: userData, error: userError } = await supabase
      .from('profiles')
      .select('id')
      .ilike('email', email)
      .single();
    
    if (userError) throw userError;
    
    if (userData && userData.id) {
      // Make a direct call to the edge function to update user metadata
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      if (token) {
        try {
          const response = await fetch(`https://qcgnklqgyzwvjrmhzwfj.supabase.co/functions/v1/update-user-metadata`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              user_id: userData.id,
              metadata: { role: 'admin', isAdmin: true }
            })
          });
          
          if (!response.ok) {
            console.warn("Could not update user metadata:", await response.text());
          }
        } catch (metadataError) {
          console.warn("Error updating user metadata:", metadataError);
        }
      }
      
      // Set up MFA for this admin user
      await requireMFA(userData.id);
    }
    
    return { 
      success: true, 
      message: `User ${email} has been updated to admin role` 
    };
  } catch (error: any) {
    console.error("Error updating user to admin:", error);
    return { 
      success: false, 
      message: `Error updating user role: ${error.message}` 
    };
  }
}

/**
 * Fetches a list of all admin users
 */
export async function getAdminUsers(): Promise<Profile[]> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'admin' as UserRole);
    
    if (error) throw error;
    
    // Ensure the data is properly typed as Profile array with UserRole
    return (data || []).map(user => ({
      ...user,
      role: user.role as UserRole // Explicitly cast role to UserRole type
    }));
  } catch (error) {
    console.error("Error fetching admin users:", error);
    return [];
  }
}

/**
 * Add a special admin email to the database
 */
export async function addSpecialAdminEmail(email: string, notes?: string): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('special_admin_emails')
      .insert([{ email, notes }]);
      
    if (error) throw error;
    
    return { 
      success: true, 
      message: `Email ${email} has been added to special admins list` 
    };
  } catch (error: any) {
    console.error("Error adding special admin email:", error);
    return { 
      success: false, 
      message: `Error adding special admin email: ${error.message}` 
    };
  }
}

/**
 * Remove a special admin email from the database
 */
export async function removeSpecialAdminEmail(email: string): Promise<{ success: boolean; message: string }> {
  try {
    const { error } = await supabase
      .from('special_admin_emails')
      .delete()
      .ilike('email', email);
      
    if (error) throw error;
    
    return { 
      success: true, 
      message: `Email ${email} has been removed from special admins list` 
    };
  } catch (error: any) {
    console.error("Error removing special admin email:", error);
    return { 
      success: false, 
      message: `Error removing special admin email: ${error.message}` 
    };
  }
}

/**
 * List all special admin emails
 */
export async function listSpecialAdminEmails(): Promise<{ email: string; notes?: string; created_at: string }[]> {
  try {
    const { data, error } = await supabase
      .from('special_admin_emails')
      .select('email, notes, created_at')
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error("Error listing special admin emails:", error);
    return [];
  }
}

/**
 * Admin verification tool - checks all possible ways a user could be an admin
 * and returns detailed information
 */
export async function verifyAdminAccess(): Promise<{
  isAdmin: boolean;
  details: {
    specialEmail: boolean;
    userMetadata: boolean;
    profileRole: boolean;
    mfaStatus: { 
      exists: boolean; 
      requires2FA: boolean; 
      completed2FA: boolean; 
      errorDetails?: string; 
    };
    errorDetails?: string;
  }
}> {
  try {
    const details = {
      specialEmail: false,
      userMetadata: false,
      profileRole: false,
      mfaStatus: { 
        exists: false, 
        requires2FA: false, 
        completed2FA: false, 
        errorDetails: undefined 
      }
    };
    
    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    const user = sessionData?.session?.user;
    
    if (!user) {
      return {
        isAdmin: false,
        details: {
          ...details,
          errorDetails: "No authenticated user found"
        }
      };
    }
    
    // Check special emails using database function
    const email = user.email?.toLowerCase();
    if (email) {
      const { data: isSpecialAdmin } = await supabase.rpc('is_special_admin_email', {
        check_email: email
      });
      
      if (isSpecialAdmin) {
        details.specialEmail = true;
      }
    }
    
    // Check user metadata
    if (user.user_metadata?.isAdmin === true || user.user_metadata?.role === "admin") {
      details.userMetadata = true;
    }
    
    // Check profile
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
        
      if (profileData?.role === 'admin') {
        details.profileRole = true;
      }
    } catch (profileError) {
      console.error("Error checking profile role:", profileError);
    }
    
    // Check MFA status
    try {
      const token = sessionData.session?.access_token;
      if (token) {
        const response = await fetch(`https://qcgnklqgyzwvjrmhzwfj.supabase.co/functions/v1/update-2fa-settings`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const mfaData = await response.json();
        if (response.ok && mfaData?.data) {
          details.mfaStatus = {
            exists: true,
            requires2FA: !!mfaData.data.requires_2fa,
            completed2FA: !!mfaData.data.has_completed_2fa,
            errorDetails: undefined
          };
        } else if (!response.ok) {
          // Store the error message in the mfaStatus
          details.mfaStatus.errorDetails = mfaData.error || "Error fetching MFA status";
        }
      }
    } catch (mfaError: any) {
      console.error("Error checking MFA status:", mfaError);
      details.mfaStatus.errorDetails = mfaError.message || "Unknown MFA error";
    }
    
    // Determine if user is admin
    const isAdmin = details.specialEmail || details.userMetadata || details.profileRole;
    
    return {
      isAdmin,
      details
    };
  } catch (error: any) {
    console.error("Error verifying admin access:", error);
    return {
      isAdmin: false,
      details: {
        specialEmail: false,
        userMetadata: false,
        profileRole: false,
        mfaStatus: { 
          exists: false, 
          requires2FA: false, 
          completed2FA: false, 
          errorDetails: error.message 
        },
        errorDetails: error.message
      }
    };
  }
}

// Import from mfaService to avoid circular dependencies
import { requireMFA } from "./mfaService";
